import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    Beaker, Calendar, CheckCircle, AlertTriangle, XCircle,
    Loader2, ChevronRight, Droplets, ShoppingCart, Eye
} from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import SEOHead from '../components/SEOHead';

export default function MySpinTouchPage() {
    const { user } = useAuth();
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    useEffect(() => {
        if (user) {
            loadAnalyses();
        }
    }, [user]);

    const loadAnalyses = async () => {
        setLoading(true);
        try {
            const data = await apiRequest('/spintouch-manual/my-analyses');
            setAnalyses(data);
        } catch (error) {
            console.error('Failed to load analyses:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'excellent':
            case 'good':
                return 'text-green-500 bg-green-500/10 border-green-500/20';
            case 'warning':
                return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
            case 'critical':
                return 'text-red-500 bg-red-500/10 border-red-500/20';
            default:
                return 'text-muted-foreground bg-muted';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'excellent':
            case 'good':
                return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'warning':
                return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
            case 'critical':
                return <XCircle className="h-5 w-5 text-red-500" />;
            default:
                return <Beaker className="h-5 w-5" />;
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'excellent': return 'Excellent';
            case 'good': return 'Bon état';
            case 'warning': return 'Attention';
            case 'critical': return 'Urgent';
            default: return 'Analysé';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleViewAnalysis = (analysis) => {
        setSelectedAnalysis(analysis);
        setDialogOpen(true);
    };

    if (!user) {
        return (
            <div className="min-h-screen pt-20 pb-12">
                <div className="max-w-4xl mx-auto px-4">
                    <Card className="glass rounded-3xl">
                        <CardContent className="py-12 text-center">
                            <Beaker className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                            <h2 className="text-2xl font-bold mb-4">Connectez-vous</h2>
                            <p className="text-muted-foreground mb-6">
                                Connectez-vous pour accéder à vos analyses SpinTouch
                            </p>
                            <Link to="/auth">
                                <Button className="rounded-full">Se connecter</Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="spintouch-page">
            <SEOHead 
                title="Mes Analyses SpinTouch | O.C.A Piscine"
                description="Consultez l'historique de vos analyses d'eau SpinTouch avec recommandations personnalisées."
            />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Beaker className="h-10 w-10 text-primary" />
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">
                            Mes Analyses SpinTouch
                        </h1>
                    </div>
                    <p className="text-muted-foreground">
                        Historique complet de vos analyses d'eau avec recommandations personnalisées
                    </p>
                </div>

                {loading ? (
                    <Card className="glass rounded-3xl">
                        <CardContent className="py-12 flex justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </CardContent>
                    </Card>
                ) : analyses.length === 0 ? (
                    <Card className="glass rounded-3xl">
                        <CardContent className="py-12 text-center">
                            <Droplets className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                            <h2 className="text-2xl font-bold mb-4">Aucune analyse</h2>
                            <p className="text-muted-foreground mb-6">
                                Vous n'avez pas encore d'analyse SpinTouch.<br/>
                                Rendez-vous chez O.C.A Piscine pour faire analyser votre eau !
                            </p>
                            <Link to="/appointment">
                                <Button className="rounded-full">
                                    Prendre rendez-vous
                                    <ChevronRight className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {analyses.map(analysis => (
                            <Card 
                                key={analysis.analysis_id}
                                className={`glass rounded-2xl border-2 cursor-pointer hover:shadow-lg transition-all ${getStatusColor(analysis.expert_analysis?.overall_status)}`}
                                onClick={() => handleViewAnalysis(analysis)}
                            >
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-xl ${getStatusColor(analysis.expert_analysis?.overall_status)}`}>
                                            {getStatusIcon(analysis.expert_analysis?.overall_status)}
                                        </div>
                                        
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Badge className={getStatusColor(analysis.expert_analysis?.overall_status)}>
                                                    {getStatusLabel(analysis.expert_analysis?.overall_status)}
                                                </Badge>
                                                {analysis.expert_analysis?.overall_score && (
                                                    <Badge variant="outline">
                                                        Score: {analysis.expert_analysis.overall_score}/100
                                                    </Badge>
                                                )}
                                                <Badge variant="secondary">{analysis.pool_type}</Badge>
                                            </div>
                                            
                                            <h3 className="font-semibold text-lg mb-1">
                                                Analyse du {formatDate(analysis.created_at)}
                                            </h3>
                                            
                                            <p className="text-muted-foreground">
                                                {analysis.expert_analysis?.summary || 'Analyse complète de votre eau de piscine'}
                                            </p>
                                            
                                            {analysis.expert_analysis?.recommendations?.length > 0 && (
                                                <p className="text-sm text-primary mt-2">
                                                    {analysis.expert_analysis.recommendations.length} recommandation(s)
                                                </p>
                                            )}
                                        </div>
                                        
                                        <Button variant="ghost" size="icon">
                                            <Eye className="h-5 w-5" />
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* CTA */}
                <Card className="glass rounded-3xl mt-8 bg-gradient-to-r from-primary/10 to-blue-500/10">
                    <CardContent className="p-8 text-center">
                        <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-primary" />
                        <h2 className="font-heading text-2xl font-bold mb-4">
                            Besoin de produits ?
                        </h2>
                        <p className="text-muted-foreground mb-6">
                            Retrouvez tous les produits recommandés dans notre boutique
                        </p>
                        <Link to="/shop">
                            <Button className="rounded-full">
                                Voir la boutique
                                <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>

            {/* Analysis Detail Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Beaker className="h-5 w-5 text-primary" />
                            Détail de l'analyse
                        </DialogTitle>
                    </DialogHeader>

                    {selectedAnalysis && (
                        <div className="space-y-6 py-4">
                            {/* Status */}
                            {selectedAnalysis.expert_analysis && (
                                <>
                                    <div className={`p-4 rounded-xl border-2 ${getStatusColor(selectedAnalysis.expert_analysis.overall_status)}`}>
                                        <div className="flex items-center gap-3">
                                            {getStatusIcon(selectedAnalysis.expert_analysis.overall_status)}
                                            <div className="flex-1">
                                                <p className="font-semibold text-lg">
                                                    {getStatusLabel(selectedAnalysis.expert_analysis.overall_status)}
                                                </p>
                                                <p className="text-sm opacity-80">
                                                    {selectedAnalysis.expert_analysis.summary}
                                                </p>
                                            </div>
                                            {selectedAnalysis.expert_analysis.overall_score && (
                                                <Badge className="text-lg px-3 py-1">
                                                    {selectedAnalysis.expert_analysis.overall_score}/100
                                                </Badge>
                                            )}
                                        </div>
                                    </div>

                                    {/* Parameters */}
                                    {selectedAnalysis.expert_analysis.parameters_analysis?.length > 0 && (
                                        <div>
                                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                                <Droplets className="h-4 w-4" />
                                                Paramètres analysés
                                            </h3>
                                            <div className="grid gap-2">
                                                {selectedAnalysis.expert_analysis.parameters_analysis.map((param, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                                                    >
                                                        {getStatusIcon(param.status)}
                                                        <div className="flex-1">
                                                            <span className="font-medium">{param.name}</span>
                                                            <span className="text-primary ml-2 font-bold">
                                                                {param.value} {param.unit}
                                                            </span>
                                                        </div>
                                                        <span className="text-sm text-muted-foreground">
                                                            Idéal: {param.ideal_range}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Issues */}
                                    {selectedAnalysis.expert_analysis.issues?.length > 0 && (
                                        <div>
                                            <h3 className="font-semibold mb-3 text-red-500 flex items-center gap-2">
                                                <AlertTriangle className="h-4 w-4" />
                                                Problèmes identifiés
                                            </h3>
                                            <div className="space-y-2">
                                                {selectedAnalysis.expert_analysis.issues.map((issue, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className="p-3 bg-red-500/10 rounded-lg border border-red-500/20"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant={issue.severity === 'critical' ? 'destructive' : 'secondary'}>
                                                                {issue.severity === 'critical' ? 'Critique' : 
                                                                 issue.severity === 'high' ? 'Important' : 'Modéré'}
                                                            </Badge>
                                                            <span className="font-medium">{issue.issue}</span>
                                                        </div>
                                                        {issue.consequence && (
                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                ⚠️ {issue.consequence}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Recommendations */}
                                    {selectedAnalysis.expert_analysis.recommendations?.length > 0 && (
                                        <div>
                                            <h3 className="font-semibold mb-3 text-primary flex items-center gap-2">
                                                <CheckCircle className="h-4 w-4" />
                                                Nos recommandations
                                            </h3>
                                            <div className="space-y-3">
                                                {selectedAnalysis.expert_analysis.recommendations.map((rec, idx) => (
                                                    <Card key={idx} className="border-primary/20">
                                                        <CardContent className="p-4">
                                                            <div className="flex items-start gap-3">
                                                                <Badge className="mt-0.5">{rec.priority}</Badge>
                                                                <div>
                                                                    <p className="font-semibold">{rec.action}</p>
                                                                    {rec.product && (
                                                                        <p className="text-sm mt-1">
                                                                            <strong>Produit recommandé:</strong> {rec.product}
                                                                        </p>
                                                                    )}
                                                                    {rec.dosage && (
                                                                        <p className="text-sm">
                                                                            <strong>Dosage:</strong> {rec.dosage}
                                                                        </p>
                                                                    )}
                                                                    {rec.timing && (
                                                                        <p className="text-sm">
                                                                            <strong>Quand:</strong> {rec.timing}
                                                                        </p>
                                                                    )}
                                                                    {rec.details && (
                                                                        <p className="text-sm text-muted-foreground mt-2">
                                                                            {rec.details}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Tips */}
                                    {selectedAnalysis.expert_analysis.tips?.length > 0 && (
                                        <div className="p-4 bg-blue-500/10 rounded-xl">
                                            <h3 className="font-semibold mb-2">💡 Conseils</h3>
                                            <ul className="space-y-1">
                                                {selectedAnalysis.expert_analysis.tips.map((tip, idx) => (
                                                    <li key={idx} className="text-sm">• {tip}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Next Test */}
                                    {selectedAnalysis.expert_analysis.next_test && (
                                        <div className="p-3 bg-muted rounded-lg text-center">
                                            <p className="text-sm">
                                                <Calendar className="h-4 w-4 inline mr-1" />
                                                <strong>Prochain test recommandé:</strong> {selectedAnalysis.expert_analysis.next_test}
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
